import { useStore } from '@nanostores/react'
import { useEffect, useState } from 'react'

import dayjs, { type Dayjs } from '@/utilities/dayjs-config'

import type { MenuTabsItem } from '@/components/NextWeekMenu/NextWeekMenuPage'
import {
  EARLIEST_MODIFIABLE_WEEK,
  SUBSCRIPTION_CUTOFF_DAY,
} from '@/constants/program-dates'
import { loadMenu } from '@/stores/menu/operations'
import { $menus } from '@/stores/menu/stores'
import { captureException } from '@/utilities/logger-fe'

const DEFAULT_ZIP = '10001'

const getNextWeeks = (date: Dayjs, weeks: number) => {
  let nextWeeks = []
  for (let i = 1; i <= weeks; i++) {
    nextWeeks.push(date.add(7 * i, 'day').startOf('week'))
  }
  return nextWeeks
}

const getWeeksData = (weekList: Dayjs[], zipcode: string): MenuTabsItem[] => {
  return weekList.map((week) => {
    const itemWeek: MenuTabsItem = {
      date: week,
      name: dayjs(week).format('YYYY-MM-DD').toString().concat(zipcode),
      label: `Week of ${dayjs(week).format('MMMM DD')}`,
      menu: [],
    }
    return itemWeek
  })
}

const useNextWeekMenu = () => {
  const startCurrentWeek = dayjs().tz().day(1) // Monday
  const nextWeeks = getNextWeeks(startCurrentWeek, 4)
  const menu = useStore($menus)
  const isAfterCutOffDay = dayjs().tz().get('day') > SUBSCRIPTION_CUTOFF_DAY
  const defaultWeekIndex = isAfterCutOffDay ? 2 : 1

  const [isLoading, setIsLoading] = useState(true)
  const [showFlyout, setShowFlyout] = useState(true)
  const [isMenuLoading, setIsMenuLoading] = useState(true)
  const [weekMenuKey, setWeekMenuKey] = useState<string>('')
  const [weekMenuTabItems, setWeekData] = useState<MenuTabsItem[] | null>([])
  const [currentWeekMenuTabItem, setCurrentWeekData] = useState<MenuTabsItem>(
    {} as MenuTabsItem,
  )

  const fetchWeekMenu = async (date: Dayjs) => {
    setIsMenuLoading(true)
    setWeekMenuKey(`${dayjs(date).format('YYYY-MM-DD')}${DEFAULT_ZIP}`)
    try {
      await loadMenu({ week: dayjs(date), zip: DEFAULT_ZIP, force: true })
    } catch (error) {
      captureException(error)
    } finally {
      setIsMenuLoading(false)
    }
  }

  useEffect(() => {
    const _weekMenu = menu[weekMenuKey]
    const _currWeek = weekMenuTabItems?.find(
      (week) => week.name === weekMenuKey,
    )

    if (!_currWeek || !_weekMenu) return

    const restWeekData =
      weekMenuTabItems?.filter((week) => week.name !== weekMenuKey) || []
    const _currWeekIndex =
      weekMenuTabItems?.findIndex((week) => week.name === weekMenuKey) || 0
    _currWeek.menu = _weekMenu.menuGroupedByDate
    restWeekData.splice(_currWeekIndex, 0, _currWeek)

    setCurrentWeekData(_currWeek)
    setWeekData(restWeekData)
    setIsMenuLoading(false)
  }, [menu])

  useEffect(() => {
    setIsLoading(true)
    try {
      fetchWeekMenu(EARLIEST_MODIFIABLE_WEEK)
    } catch (error) {
      captureException(error)
    } finally {
      const weekList = [startCurrentWeek, ...nextWeeks]
      const weekMenuTabItems = getWeeksData(weekList, DEFAULT_ZIP)
      setWeekData(weekMenuTabItems)
      setIsLoading(false)
    }
  }, [])

  return {
    weekMenuTabItems,
    currentWeekMenuTabItem,
    fetchWeekMenu,
    isLoading,
    isMenuLoading,
    showFlyout,
    setShowFlyout,
    defaultWeekIndex,
  }
}

export default useNextWeekMenu
